import type React from 'react';

import { Row, Col, Button } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import DataGrid from 'components/theme_components/DataGrid';
import HeroTitle from 'components/theme_components/HeroTitle';
import DF from 'DisplayFramework';
import {
  DownloadOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useContext, useEffect, useState } from 'react';
import Notifications from 'components/theme_components/Notifications';
import type { FilterConfig, FilterValues } from 'types';
import { useQuery } from 'react-query';
import {
  type ProductConfig,
  type ParRateFormulaResponse,
  deleteParRateFormula,
  getProductConfigList,
} from 'services/parametersService';
import GetLabel from 'DisplayFramework/components/GetLabel';
import { AuthContext } from 'components/Auth/Auth';
import ConfirmModal from 'components/theme_components/ThirdParty/ConfirmModal';
import { useNavigate } from 'react-router-dom';
import type { TableRowSelection } from 'antd/es/table/interface';

export type ProductFamilyListProps = {
  color: string;
};

const ProductFamilyList = (props: ProductFamilyListProps) => {
  const [filterValues, setFilterValues] = useState<FilterValues>({
    prcode: '',
    financedcategory: '',
  });
  const [form] = useForm();

  // Initialize form values
  useEffect(() => {
    form.setFieldsValue({
      prcode: filterValues.prcode,
      financedcategory: filterValues.financedcategory,
    });
  }, [form]);
  const [isDisabled, setIsDisabled] = useState(true);
  const [selectedRow, setSelectedRow] = useState<ParRateFormulaResponse | null>(
    null
  );
  const [selectedRowKey, setSelectedRowKey] = useState<React.Key | null>(null);

  const navigate = useNavigate();
  const { isInspect } = useContext(AuthContext);
  const [modalVisibleDelet, setmodalVisibleDelete] = useState(false);

  const getSearchParams = () => {
    return {
      prcode: filterValues.prcode || '',
      financedcategory: filterValues.financedcategory ?? '',
    };
  };

  const initialSearchParams: ProductConfig | any = getSearchParams();

  const { data, refetch } = useQuery(
    ['getProductConfigList', filterValues],
    () =>
      getProductConfigList({
        prcode: filterValues.prcode || '',
        financedcategory: filterValues.financedcategory || '',
      })
        .then((res) => res.list || [])
        .catch((res) => []),
    {
      select: (data) => data.sort((a, b) => b.id - a.id),
    }
  );

  const handleFilterSearch = (values: any) => {
    console.log('Filter values:', values);
    setFilterValues(values);
    refetch();
  };


  const handleFilterReset = () => {
    setFilterValues({ prcode: '', financedcategory: '' });
    form.resetFields();
  };
  const filterConfig: FilterConfig[] = [
    {
      key: 'FINCAT',
      label: 'financedcategory',
      type: 'select',
    },
    {
      key: 'prcode',
      label: 'prcode',
      type: 'text',
    },
  ];

  const columns = [
    {
      comcode: 'financedcategory',
      dataIndex: 'financedcategorylabel',
      key: 'financedcategorylabel',
      width: '100px',
      render: (text: string) => (
        <div style={{ paddingLeft: '12px' }}>{text}</div>
      ),
    },
    {
      comcode: 'reference',
      dataIndex: 'prcode',
      key: 'prcode',
      width: '100px',
      render: (text: string) => (
        <div style={{ paddingLeft: '12px' }}>{text}</div>
      ),
    },
    {
      comcode: 'description',
      dataIndex: 'prlabel',
      key: 'prlabel',
      width: '200px',
      render: (text: string) => (
        <div style={{ paddingLeft: '12px' }}>{text}</div>
      ),
    },

    {
      title: '',
      key: 'arrow',
      width: '80px',
      render: (record: ProductConfig) => (
        <RightOutlined
          onClick={() =>
            navigate(`/configuration-sfs/productsfamily/${record.prcode}`)
          }
        />
      ),
      onHeaderCell: () => ({
        style: { paddingLeft: '12px' },
      }),
    },
  ];

  const onSelectChange = (
    selectedRowKeys: React.Key[],
    selectedRows: ParRateFormulaResponse[]
  ) => {
    if (selectedRows.length > 0) {
      const lastSelected = selectedRows[selectedRows.length - 1];
      setSelectedRow(lastSelected);
      setSelectedRowKey(lastSelected.formulacode);
      setIsDisabled(false);
    } else {
      setSelectedRow(null);
      setSelectedRowKey(null);
      setIsDisabled(true);
    }
  };

  const rowSelection: TableRowSelection<ParRateFormulaResponse> = {
    type: 'checkbox' as const,
    selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
    onChange: onSelectChange,
    onSelect: (record: ParRateFormulaResponse, selected: boolean) => {
      if (selected) {
        setSelectedRow(record);
        setSelectedRowKey(record.formulacode);
        setIsDisabled(false);
      } else {
        setSelectedRow(null);
        setSelectedRowKey(null);
        setIsDisabled(true);
      }
    },
  };

  const handleDelete = () => {
    if (selectedRow) {
      deleteParRateFormula({
        formulacode: selectedRow.formulacode,
      }).then((response) => {
        if (response.code === '200') {
          Notifications({
            status: 'success',
            message: response.message,
            description: response.description,
            duration: 2,
            placement: 'top',
          });
          setSelectedRow(null);
          setSelectedRowKey(null);
          setIsDisabled(true);
          refetch();
        } else {
          Notifications({
            status: 'error',
            message: response.message,
            description: response.description,
            duration: 2,
            placement: 'top',
          });
        }
      });
      !isInspect && setmodalVisibleDelete(false);
    }
  };

  return (
    <DF.Provider pagcode="productlist">
      <div className="bg-color-gray">
        <HeroTitle
          form={form}
          color={props.color}
          title="ProductFamilyList"
          showFilter
          display
          filterConfig={filterConfig}
          onFilterSearch={handleFilterSearch}
          onFilterReset={handleFilterReset}
          className="productlist"
          noTitleOverlay
        />

        <div className="ut-title-page no-after">
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: '15px' }}
          >
            <Col className="d-flex justify-start align-center">
              {/* <Button
                disabled={isDisabled}
                className={
                  !isDisabled
                    ? 'no-border-radius btn-header ant-btn-outline-primary bg-color-hover'
                    : 'no-border-radius  btn-header '
                }
                icon={
                  <IBasket
                    color={isDisabled ? '#d9d9d9' : '#dd7248'}
                    className="font-size-25"
                    onClick={() => {
                      isInspect && setmodalVisibleDelete(true);
                    }}
                  />
                }
                onClick={() => {
                  !isInspect && setmodalVisibleDelete(true);
                }}
              >
                <GetLabel label="deletproductfamily" /> ({selectedRow ? 1 : 0})
              </Button> */}

              <Button
                className="no-border-radius ant-btn-outline-primary ml-2 filter-item  btn-header bg-color-hover"
                icon={<PlusOutlined onClick={() => isInspect} />}
                onClick={() =>
                  !isInspect &&
                  navigate(`/configuration-sfs/productsfamily/create`)
                }
              >
                <GetLabel label="addproductfamily" />
              </Button>
            </Col>
            <Col span={5} className="d-flex justify-end align-center">
              <Button
                onClick={() => {}}
                className="ut-reset-btn ut-btn-no-border no-box-shadow"
                icon={<DownloadOutlined />}
              >
                <GetLabel
                  label="Exporter(.xls)"
                  className="color-blue font-weight-500"
                />
              </Button>
            </Col>
          </Row>

          <DataGrid
            columns={columns}
            dataSource={data as any}
            rowKey="prcode"
            hideFilters
            hideTitle
            // rowSelection={rowSelection}
            pagination={{
              pageSize: 10,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total}`,
              showSizeChanger: true,
            }}
            className="no-border-radius"
          />
        </div>
        <ConfirmModal
          className="modalConfirmOrCancel"
          visible={modalVisibleDelet}
          status="error"
          message="Êtes-vous sûr de vouloir supprimer ce Produit ?"
          onConfirm={handleDelete}
          onCancel={() => {
            !isInspect && setmodalVisibleDelete(false);
          }}
          Confirmcomcode="valider"
          Cancelcomcode="close"
        />
      </div>
    </DF.Provider>
  );
};

export default ProductFamilyList;
